import React, { useCallback, useRef, useState } from 'react';

import { openLabBillPdf } from '../services/api/admissionDischargeApi';
import { isInvoiceBearing } from '../constants/admission';
import ConfirmationModal from '../components/common/ConfirmationModal/ConfirmationModal';

/**
 * useDiagnosticsBillPrint (F1, D31/D32) — the ONE place that owns the
 * "Generate Diagnostics bill" action for every mount (RunningBillPanel,
 * DischargeDialog via AdmissionWorkspace, DiagnosticsView, FinalBillView —
 * F2/F3). Do not fork this — a second copy of the confirm-before-mint /
 * printing / error logic is the same drift D29 already banned for
 * DiagnosticsCard / OrderLabDialog; `labSurfaceSingleHome.test.js` guards it.
 *
 * `generate(admissionStatus)` takes the status at CALL time, not at hook
 * mount time — DischargeDialog tracks its own `currentStatus` locally
 * (flipped synchronously the moment `Initiate discharge` succeeds, ahead of
 * the parent desk's own admission-row refetch, FIX-3) and its success branch
 * knows the just-completed status directly. Binding the confirm decision to
 * a status captured once at mount would race that local flip and could mint
 * a number with NO warning shown. Every caller passes the freshest status it
 * holds at click time.
 *
 * - While the admission is NOT invoice-bearing (`isInvoiceBearing`,
 *   `constants/admission.js` — never an inline status literal): the button
 *   prints the INTERIM sheet straight away, no confirmation — the server
 *   mints no number in this state (build-plan D-4).
 * - Once invoice-bearing (DISCHARGE_INITIATED / BILLING_CLEARANCE /
 *   DISCHARGED / DECEASED): a call mints a permanent serial the first time
 *   (a resumed screen may only ever see a REPLAY, but the FE cannot tell
 *   first-mint from replay client-side, so it warns on every generate while
 *   invoice-bearing, per the P0 ruling). Confirmed via the shared
 *   `ConfirmationModal` — never `window.confirm`.
 * - `openLabBillPdf` blob-parses both the 404 `NO_LAB_CHARGES` and the 409
 *   `DIAG_BILL_NOT_ISSUABLE` (cancelled / transferred-out admission, D-4);
 *   this hook only ever surfaces the mapped `error` string, never a raw
 *   server response reaching the user.
 * - `generate()` is both fire-and-forget (a caller with no printing UI of
 *   its own — RunningBillPanel, DiagnosticsView, FinalBillView — just calls
 *   it and reads back this hook's own `printing`/`error`) AND
 *   promise-returning (DischargeDialog already runs ONE generic `runPrint`
 *   for both its Print-invoice and Generate-Diagnostics-bill buttons — that
 *   plumbing is pre-existing UI chrome, not a second copy of this logic —
 *   `generate()` resolves `{success, error}` for it, or `undefined` on a
 *   cancelled confirmation, which `runPrint` already treats as a no-op).
 *
 * RATIFIED (integration gate, copy pack) — Business signed off this exact
 * copy; do not re-word. Deliberately does NOT claim "no changes after this" —
 * the lock comes from discharge-initiation, not from minting this bill.
 */
const MINT_WARNING_TITLE = 'Generate diagnostics bill?';
const MINT_WARNING_MESSAGE = 'This bill will be given a permanent number. A numbered bill cannot be renumbered or cancelled — if you need another copy, print it again.';

/**
 * bk-728 (owner report) — "the diagnostic button is saying Generate
 * Diagnostic bill, instead it should say Interim Diagnostic until discharge
 * is initiated, and after that it should say Diagnostic bill": the button
 * must say what it will PRODUCE, not promise a "bill" while handing back an
 * interim sheet. Driven by the SAME `isInvoiceBearing` gate the D-4 mint
 * decision above uses (never a second status check invented here), and
 * worded to AGREE with the sheet's own printed title
 * (`_build_lab_bill_html`, discharge_documents_pdf.py: "DIAGNOSTICS BILL
 * (INTERIM)" while not invoice-bearing, plain "DIAGNOSTICS BILL" once
 * minted) — never a second vocabulary for the same document.
 *
 * NEEDS-OWNER-CONFIRM — the owner's own phrasing was singular ("Interim
 * Diagnostic" / "Diagnostic bill"); this uses the sheet's own plural
 * ("Diagnostics") so the button and the printed title read as the SAME
 * noun. Also keeps "Bill" on the interim label (the owner's phrase dropped
 * it) since the sheet's own interim title is "DIAGNOSTICS BILL (INTERIM)",
 * not "DIAGNOSTICS (INTERIM)". Confirm before ship.
 *
 * ONE place — every mount (RunningBillPanel live + final-bill, DiagnosticsView,
 * DischargeDialog) calls this rather than re-deriving its own copy; the same
 * drift `labSurfaceSingleHome.test.js` already guards for the hook itself.
 */
export function diagnosticsBillLabel(admissionStatus) {
  return isInvoiceBearing(admissionStatus) ? 'Diagnostics Bill' : 'Interim Diagnostics Bill';
}

export default function useDiagnosticsBillPrint(admissionId) {
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const printingRef = useRef(false);
  const pendingResolveRef = useRef(null);
  const pendingStatusRef = useRef(undefined);

  // QA-CTF8 (bk-728 follow-up) — the viewer's own header AND the download
  // FILENAME come from `openLabBillPdf`'s `title`, which defaulted to the
  // numbered noun in BOTH states: an INTERIM sheet opened under a header
  // reading "Diagnostics Bill" and saved to disk as `Diagnostics_Bill.pdf`,
  // disagreeing with both the button and the sheet's own printed
  // "DIAGNOSTICS BILL (INTERIM)" title. Fed from the SAME
  // `diagnosticsBillLabel` the button uses — never a third vocabulary, and
  // whatever string the owner finally ratifies moves all three at once.
  const runGenerate = useCallback(async (admissionStatus) => {
    if (printingRef.current) return undefined;
    printingRef.current = true;
    setPrinting(true);
    setError(null);
    const res = await openLabBillPdf(admissionId, diagnosticsBillLabel(admissionStatus));
    printingRef.current = false;
    setPrinting(false);
    if (!res.success) setError(res.error || 'Could not open the diagnostics bill.');
    return res;
  }, [admissionId]);

  // The confirm-before-mint decision — SERVER-derived admission status,
  // read fresh at call time, never a second guess of what the server will do.
  const generate = useCallback((admissionStatus) => {
    if (printingRef.current) return Promise.resolve(undefined);
    if (isInvoiceBearing(admissionStatus)) {
      pendingStatusRef.current = admissionStatus;
      return new Promise((resolve) => {
        pendingResolveRef.current = resolve;
        setConfirmOpen(true);
      });
    }
    return runGenerate(admissionStatus);
  }, [runGenerate]);

  const confirmGenerate = useCallback(async () => {
    setConfirmOpen(false);
    const res = await runGenerate(pendingStatusRef.current);
    if (pendingResolveRef.current) { pendingResolveRef.current(res); pendingResolveRef.current = null; }
  }, [runGenerate]);

  const cancelGenerate = useCallback(() => {
    setConfirmOpen(false);
    if (pendingResolveRef.current) { pendingResolveRef.current(undefined); pendingResolveRef.current = null; }
  }, []);

  // Single-sourced JSX — every mount renders THIS element rather than
  // re-declaring its own `<ConfirmationModal>` with the same copy, so the
  // warning can only ever exist in one place. Mounted ONLY while actually
  // open (never `isOpen={false}` sitting in the tree) — `ConfirmationModal`
  // reads `useTheme()` unconditionally, so an always-mounted copy would
  // require every non-invoice-bearing screen to carry a `ThemeProvider` it
  // has no other reason to need.
  const confirmModal = confirmOpen ? (
    <ConfirmationModal
      isOpen
      title={MINT_WARNING_TITLE}
      message={MINT_WARNING_MESSAGE}
      ctaLabel="Generate bill"
      cancelLabel="Cancel"
      onConfirm={confirmGenerate}
      onCancel={cancelGenerate}
    />
  ) : null;

  return { generate, printing, error, confirmModal };
}
